import React, { useState, useContext } from "react"
import { Typography } from "@stardust-ds/react"
import * as yup from "yup"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import intl from "react-intl-universal"

import styles from "./index.module.scss"
import EvidenceTextCard from "../../atoms/EvidenceTextCard"
import { fnFormatDate } from "../../../utils/date"
import EvidencesCard from "../../atoms/EvidenceCard"
import ModalConfirmDelete from "../ModalAddPainelCancel"
import RcmpModalEditComment from "../../molecules/ModalEditComment"
import AuthContext from "../../../contexts/Auth"
import RcmpLoading from "../../atoms/Loading"

function fnSortByCreateDate(a, b) {
  return new Date(b?.opae_dcreated) - new Date(a?.opae_dcreated)
}

function RcmpTextEvidente(props) {
  const { oEvidence, ...oRest } = props

  if (oEvidence?.opae_ctype !== "TEXT") return null

  const oFormtedEvidence = {
    ...oEvidence,
    opae_cvalue: oEvidence?.opae_cvalue,
    dDate: fnFormatDate(oEvidence?.opae_dcreated),
    cUserName: oEvidence?.user?.user_cname,
  }

  return <EvidenceTextCard oData={oFormtedEvidence} {...oRest} />
}

function RcmpFileEvidente(props) {
  const { oEvidence, ...oRest } = props

  if (oEvidence?.opae_ctype !== "FILE" && oEvidence?.opae_ctype !== "MEDIA")
    return null

  const oFormtedEvidence = {
    ...oEvidence,
    name: oEvidence?.opae_cfilename,
    date: fnFormatDate(oEvidence?.opae_dcreated),
    user: oEvidence?.user?.user_cname,
    download: oEvidence?.opae_cvalue,
  }

  return <EvidencesCard data={oFormtedEvidence} {...oRest} />
}

function RcmpGEDEvidente(props) {
  const { oEvidence, ...oRest } = props

  if (oEvidence?.opae_ctype !== "GED") return null

  const oFormtedEvidence = {
    ...oEvidence,
    name: oEvidence?.ged?.doc_ctitle,
    date: fnFormatDate(oEvidence?.opae_dcreated),
    user: oEvidence?.user?.user_cname,
    redirect: `/document/detail/${oEvidence?.ged?.doc_nid}`,
  }

  return <EvidencesCard data={oFormtedEvidence} {...oRest} />
}

export default function RcmpActivityEvidencesList(props) {
  const {
    aEvidences,
    fnSetNEvidenceIdDelete,
    bIsUploaded,
    bReadOnly,
    fnEditEvidenceSubmit,
  } = props

  const [oCurrentEvidence, fnSetOCurrentEvidence] = useState(null)
  const [bOpenEditTextModal, fnSetBOpenEditTextModal] = useState(false)
  const [bOpenDeleteModal, fnSetBOpenDeleteModal] = useState(false)

  const { oUser } = useContext(AuthContext)

  const schemaText = yup.object().shape({
    cEditText: yup.string().required(intl.get("form.required_field")),
  })

  const { register, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schemaText),
  })

  function fnHandleOpenEditTextModal(oEvidence) {
    fnSetOCurrentEvidence(oEvidence)
    fnSetBOpenEditTextModal(true)
  }

  async function fnHandleEditText(cEvidenceText) {
    await fnEditEvidenceSubmit(oCurrentEvidence?.opae_nid, {
      opae_cvalue: cEvidenceText.cEditText,
    })
    fnSetBOpenEditTextModal(false)
  }

  function fnHandleCancelEditText() {
    fnSetBOpenEditTextModal(false)
  }

  function fnHandleOpenDeleteModalEvicente(oEvidence) {
    fnSetOCurrentEvidence(oEvidence)
    fnSetBOpenDeleteModal(true)
  }

  function fnHandleDeleteEvidence(nEvidenceId) {
    fnSetNEvidenceIdDelete(nEvidenceId)
  }

  const nUserId = oUser?.data?.user_nid || ""

  function fnRenderEvidences() {
    if (!aEvidences?.length) {
      return (
        <div className={styles.noEvidence}>
          <Typography type="p2" color="#415364" textAlign="left">
            {intl.get("ActivityEvidence.no_evidences")}
          </Typography>
        </div>
      )
    }

    return (
      <>
        {aEvidences.sort(fnSortByCreateDate).map((oEvidence, index) => (
          <React.Fragment key={index}>
            <RcmpTextEvidente
              oEvidence={oEvidence}
              nUserId={nUserId}
              fnEditEvidence={fnHandleOpenEditTextModal}
              fnDeleteEvidence={fnHandleOpenDeleteModalEvicente}
              bShowActionButtons={!bReadOnly && nUserId === oEvidence?.user?.user_nid}
            />
            <RcmpFileEvidente
              oEvidence={oEvidence}
              removeFile={fnHandleOpenDeleteModalEvicente}
              bShowRemoveButton={!bReadOnly && nUserId === oEvidence?.user?.user_nid}
            />
            <RcmpGEDEvidente
              oEvidence={oEvidence}
              removeFile={fnHandleOpenDeleteModalEvicente}
              bShowRemoveButton={!bReadOnly && nUserId === oEvidence?.user?.user_nid}
            />
          </React.Fragment>
        ))}
      </>
    )
  }

  const cEvidence = oCurrentEvidence?.ged?.doc_ctitle || oCurrentEvidence?.opae_cfilename || oCurrentEvidence?.opae_cvalue

  return (
    <div className={styles.evidencesList}>
      <Typography
        color="#415364"
        type="l3"
        textAlign="left"
        className={styles.evidenceLabel}
      >
        {intl.get("ActivityEvidence.evidences_sent")}
      </Typography>
      {
        <div className={styles.spinnerContainer}>
          <RcmpLoading bShow={bIsUploaded} size={50} />
        </div>
      }
      {fnRenderEvidences()}
      <RcmpModalEditComment
        bIsOpen={bOpenEditTextModal}
        fnCancelEdit={fnHandleCancelEditText}
        cOldValue={oCurrentEvidence?.opae_cvalue}
        fnHandleConfirm={fnHandleEditText}
        register={register}
        handleSubmit={handleSubmit}
        setValue={setValue}
        label={intl.get("ActivityEvidence.edit_text")}
      />
      <ModalConfirmDelete
        fnOnClose={fnSetBOpenDeleteModal}
        isOpen={bOpenDeleteModal}
        headerText={intl.get("ActivityEvidence.delete_evidence")}
        bOnDelete={true}
        bHasPassword={false}
        idPainel={oCurrentEvidence?.opae_nid}
        text={intl.get("ActivityEvidence.confirm_delete_evidence", { evidence: cEvidence })}
        fnHandleDelete={fnHandleDeleteEvidence}
      />
    </div>
  )
}
